"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import MentionTextArea from "@/components/mentions/MentionTextArea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { motion } from "framer-motion";
import { useToast } from "@/components/ui/use-toast";
import { FiImage } from "react-icons/fi";
import { BasicUserType } from "@/types/user";
import { TbChecklist } from "react-icons/tb";
import { Input } from "@/components/ui/input";
import { Plus, X, Play, Target, Ticket, Calendar, MapPin, Bold, Italic, Apple, Dumbbell } from "lucide-react";
import { CreateEventModal } from "@/components/modals/create-event-modal";
import { CreateChallengeModal } from "@/components/challenges/CreateChallengeModal";
import { SchedulePostModal } from "@/components/modals/schedule-post-modal";
import { DraftsModal } from "@/components/modals/drafts-modal";
import CreateLocationModal from "@/components/modals/create-location-modal";
import { CreateMealModal } from "@/components/modals/create-meal-modal";
import { apiClient } from "@/lib/api-client";
import { SimplifiedMealModal } from "@/components/modals/simplified-meal-modal";
import { ExerciseFormAnalyzerModal } from "@/components/modals/exercise-form-analyzer-modal";
import { AnalysisResult } from "@/lib/exercise-form-analyzer-client";
import { PostType } from "@/types/post";
// Socket functionality removed - using API calls only

interface CreatePostProps {
  handlePost: Function;
  text?: string;
  user: BasicUserType | null;
}

interface MediaFile {
  url: string;
  type: string;
}

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB limit

const CreatePost = ({
  handlePost,
  user,
  text = "What's happening?",
}: CreatePostProps) => {
  const [content, setContent] = useState("");
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [isPollActive, setIsPollActive] = useState(false);
  const [isChallengeModalOpen, setIsChallengeModalOpen] = useState(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isDraftsModalOpen, setIsDraftsModalOpen] = useState(false);
  const [isMealModalOpen, setIsMealModalOpen] = useState(false);
  const [isSimplifiedMealModalOpen, setIsSimplifiedMealModalOpen] = useState(false);
  const [isExerciseFormAnalyzerModalOpen, setIsExerciseFormAnalyzerModalOpen] = useState(false);
  const [exerciseAnalysisResult, setExerciseAnalysisResult] = useState<AnalysisResult | null>(null);
  const [scheduledTime, setScheduledTime] = useState<Date | null>(null);
  const [scheduledTimezone, setScheduledTimezone] = useState<string>(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [drafts, setDrafts] = useState<PostType[]>([]);
  const [scheduledPosts, setScheduledPosts] = useState<PostType[]>([]);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [location, setLocation] = useState<{
    name: string;
    address: string;
    coordinates: {
      lat: number;
      lng: number;
    };
  } | null>(null);
  const [pollOptions, setPollOptions] = useState<string[]>(["", ""]);
  const [pollDuration, setPollDuration] = useState({
    days: 1,
    hours: 0,
    minutes: 0
  });
  const { toast } = useToast();

  const loadSavedPosts = async () => {
    try {
      const data = await apiClient.getSavedPosts();
      // Filter posts by status since API returns all saved posts
      const allPosts = data.posts || [];
      setDrafts(allPosts.filter((post: any) => post.status === 'draft') as unknown as PostType[]);
      setScheduledPosts(allPosts.filter((post: any) => post.status === 'scheduled') as unknown as PostType[]);
    } catch (error) {
      console.error('Error loading saved posts:', error);
    }
  };

  // Load drafts and scheduled posts on mount
  useEffect(() => {
    loadSavedPosts();
    // Socket functionality removed - real-time updates will be handled via polling or backend push
  }, []);

  const handleEditPost = (post: PostType) => {
    setContent(post.content);
    if (post.scheduledFor) {
      setScheduledTime(new Date(post.scheduledFor));
    }
    setIsDraftsModalOpen(false);
  };

  const handleDeletePost = async (post: PostType) => {
    try {
      await fetch(`${process?.env?.NEXT_PUBLIC_API_URL}/posts/${post.id}`, { method: 'DELETE' });
      setDrafts(drafts.filter(d => d.id !== post.id));
      toast({ title: "Draft deleted successfully" });
    } catch (error) {
      console.error('Error deleting draft:', error);
      toast({ 
        title: "Failed to delete draft",
        variant: "destructive"
      });
    }
  };

  const handleCancelScheduled = async (post: PostType) => {
    try {
      await fetch(`${process?.env?.NEXT_PUBLIC_API_URL}/posts/${post.id}/cancel-schedule`, { method: 'POST' });
      setScheduledPosts(scheduledPosts.filter(p => p.id !== post.id));
      toast({ title: "Post unscheduled successfully" });
    } catch (error) {
      console.error('Error canceling scheduled post:', error);
      toast({ 
        title: "Failed to unschedule post",
        variant: "destructive"
      });
    }
  };

  const handleSubmit = async () => {
    if (!user) {
      toast({ title: "Please log in to post" });
      return;
    }

    if (content.length === 0) {
      toast({ title: "Content must not be empty!" });
      return;
    }

    if (isPollActive) {
      const validOptions = pollOptions.filter(option => option.trim() !== "");
      if (validOptions.length < 2) {
        toast({ title: "Please add at least 2 options" });
        return;
      }

      const totalMinutes = 
        (pollDuration.days * 24 * 60) + 
        (pollDuration.hours * 60) + 
        pollDuration.minutes;

      if (totalMinutes <= 0) {
        toast({ title: "Poll duration must be greater than 0" });
        return;
      }

      const endTime = new Date();
      endTime.setMinutes(endTime.getMinutes() + totalMinutes);

      try {
        const response = await handlePost({
          userId: user.id,
          name: user.name || "",
          username: user.username || "",
          image: user.image || "",
          content: content,
          type: "poll",
          likes: [],
          reposts: [],
          replies: [],
          files: [],
          views: [],
          viewCount: 0,
          originalPostId: null,
          communityId: null,
          poll: {
            options: Array.isArray(validOptions) ? validOptions.map(text => ({ text })) : [],
            endTime
          }
        });

        setContent("");
        setPollOptions(["", ""]);
        setIsPollActive(false);
        setPollDuration({ days: 1, hours: 0, minutes: 0 });

        toast({
          title: "Poll created successfully!",
        });
      } catch (error) {
        console.error('Error creating poll:', error);
        toast({
          title: "Failed to create poll",
          description: "Please try again later",
          variant: "destructive",
        });
      }
    } else {
      try {
        let response;
        
        if (scheduledTime && scheduledTimezone) {
          // Use scheduled posts endpoint
          response = await apiClient.createScheduledPost({
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              content,
              scheduledFor: scheduledTime,
              timezone: scheduledTimezone,
              medias: files,
              ...(location && {
                locationName: location.name,
                locationLat: location?.coordinates?.lat,
                locationLng: location?.coordinates?.lng
              })
            }),
          });
        } else {
          // Regular post
          response = await handlePost({
            userId: user.id,
            name: user.name || "",
            username: user.username || "",
            image: user.image || "",
            content,
            type: "post",
            likes: [],
            reposts: [],
            replies: [],
            medias: files,
            views: [],
            viewCount: 0,
            originalPostId: null,
            communityId: null,
            ...(location && {
              locationName: location.name,
              locationLat: location?.coordinates?.lat,
              locationLng: location?.coordinates?.lng
            })
          });
        }

        setContent("");
        setFiles([]);
        setLocation(null);
        
        if (scheduledTime && scheduledTimezone) {
          // Refresh the scheduled posts list
          const loadSavedPosts = async () => {
            try {
              const data = await apiClient.getSavedPosts();
              const allPosts = data.posts || [];
              setDrafts(allPosts.filter((post: any) => post.status === 'draft') as unknown as PostType[]);
              setScheduledPosts(allPosts.filter((post: any) => post.status === 'scheduled') as unknown as PostType[]);
            } catch (error) {
              console.error('Error loading saved posts:', error);
            }
          };
          loadSavedPosts();
          
          toast({
            title: "Post scheduled!",
            description: `Will be posted on ${scheduledTime.toLocaleDateString(undefined, {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })} at ${scheduledTime.toLocaleTimeString(undefined, {
              hour: '2-digit',
              minute: '2-digit'
            })} (${formatTimezoneDisplay(scheduledTimezone)})`
          });
        } else {
          toast({
            title: "Successfully sent!"
          });
        }
        
        setScheduledTime(null);
        setScheduledTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
      } catch (error) {
        console.error('Error creating post:', error);
        toast({
          title: "Failed to create post",
          description: "Please try again later",
          variant: "destructive",
        });
      }
    }
  };

  const addPollOption = () => {
    if (pollOptions.length < 4) {
      setPollOptions([...pollOptions, ""]);
    }
  };

  const removePollOption = (index: number) => {
    if (pollOptions.length > 2) {
      const newOptions = [...pollOptions];
      newOptions.splice(index, 1);
      setPollOptions(newOptions);
    }
  };

  const handleFileUpload = (file: File) => {
    // Increase size limit to 5MB like Twitter
    const TWITTER_MAX_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > TWITTER_MAX_SIZE) {
      toast({
        title: "File too large",
        description: "Please upload files smaller than 5MB",
        variant: "destructive"
      });
      return;
    }

    if (file?.type?.startsWith('image/')) {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = () => {
        img.src = reader.result as string;
      };

      img.onload = () => {
        // Twitter-like max dimensions while maintaining aspect ratio
        const MAX_DIMENSION = 2048;
        let width = img.width;
        let height = img.height;

        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          if (width > height) {
            height = Math.round((height * MAX_DIMENSION) / width);
            width = MAX_DIMENSION;
          } else {
            width = Math.round((width * MAX_DIMENSION) / height);
            height = MAX_DIMENSION;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        // Use high-quality image smoothing
        if (ctx) {
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);

          // Use progressive JPEG for better loading
          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.92);
          setFiles([...files, { url: compressedDataUrl, type: 'image/jpeg' }]);
        }
      };

      reader.readAsDataURL(file);
    } else {
      // For non-image files, process normally
      const reader = new FileReader();
      reader.onload = () => {
        const buffer = Buffer.from(reader.result as ArrayBuffer);
        const base64Data = buffer.toString("base64");
        const dataUrl = `data:${file.type};base64,${base64Data}`;
        setFiles([...files, { url: dataUrl, type: file.type }]);
      };

      reader.readAsArrayBuffer(file);
    }
  };

  const isVideo = (type: string) => type.startsWith('video/');

  const formatTimezoneDisplay = (timezone: string) => {
    const cityName = timezone.split('/').pop()?.replace('_', ' ');
    const tzAbbr = new Date().toLocaleString('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    }).split(' ').pop();
    return `${cityName} - ${tzAbbr}`;
  };

  if (!user) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card className="mb-4">
        <CardContent className="flex flex-row space-x-4 items-start pt-4 px-4">
          <Avatar className="h-10 w-10 flex-shrink-0">
            <AvatarImage src={user.image || ""} alt={user.name || "User"} />
            <AvatarFallback className="text-lg">
              {user.name?.[0] || "U"}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col w-full">
            <div className="space-y-2 w-full">
              <MentionTextArea
                value={content}
                onChange={setContent}
                placeholder={text}
                className={`flex-grow text-[16px] resize-none min-h-[10vh] border-none p-0 ${
                  isBold ? 'font-bold' : ''
                } ${
                  isItalic ? 'italic' : ''
                }`}
                rows={3}
              />
              {scheduledTime && scheduledTimezone && (
                <div className="text-sm text-muted-foreground bg-muted/30 rounded-md p-2">
                  This post will be scheduled for {scheduledTime.toLocaleDateString(undefined, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })} at {scheduledTime.toLocaleTimeString(undefined, {
                    hour: '2-digit',
                    minute: '2-digit'
                  })} ({formatTimezoneDisplay(scheduledTimezone)})
                </div>
              )}
              {location && (
                <div className="text-sm text-muted-foreground bg-muted/30 rounded-md p-2 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <span>{location.name}</span>
                </div>
              )}
            </div>

            {isPollActive ? (
              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Poll Options</label>
                  <div className="space-y-2">
                    {pollOptions?.map((option, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          placeholder={`Option ${index + 1}`}
                          value={option}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const newOptions = [...pollOptions];
                            newOptions[index] = e?.target?.value;
                            setPollOptions(newOptions);
                          }}
                          className="flex-1"
                        />
                        {index >= 2 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removePollOption(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    {pollOptions.length < 4 && (
                      <Button
                        variant="outline"
                        onClick={addPollOption}
                        className="w-full mt-2"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Option
                      </Button>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Poll Length</label>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-sm text-gray-500">Days</label>
                      <Input
                        type="number"
                        min="0"
                        max="7"
                        value={pollDuration.days}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPollDuration({
                          ...pollDuration,
                          days: parseInt(e?.target?.value) || 0
                        })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-500">Hours</label>
                      <Input
                        type="number"
                        min="0"
                        max="23"
                        value={pollDuration.hours}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPollDuration({
                          ...pollDuration,
                          hours: parseInt(e?.target?.value) || 0
                        })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-500">Minutes</label>
                      <Input
                        type="number"
                        min="0"
                        max="59"
                        value={pollDuration.minutes}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPollDuration({
                          ...pollDuration,
                          minutes: parseInt(e?.target?.value) || 0
                        })}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className={`grid gap-0.5 mt-2 ${
                files.length === 1 ? 'grid-cols-1' :
                files.length === 2 ? 'grid-cols-2' :
                files.length === 3 ? 'grid-cols-2' :
                'grid-cols-2'
              }`}>
                {files?.map((file, index) => (
                  <div key={index} className={`relative ${
                    files.length === 3 && index === 0 ? 'row-span-2' : ''
                  }`}>
                    {isVideo(file.type) ? (
                      <div className="relative w-full h-full">
                        <video
                          src={file.url}
                          className={`rounded-2xl w-full ${
                            files.length === 1 ? 'h-auto max-h-[510px] object-contain bg-black' :
                            files.length === 2 ? 'h-[250px] object-cover' :
                            files.length === 3 ? (index === 0 ? 'h-[250px]' : 'h-[124px]') : 'h-[124px]'
                          } object-cover`}
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Play className="w-8 h-8 text-white bg-black/50 rounded-full p-1" />
                        </div>
                      </div>
                    ) : (
                      <img
                        src={file.url}
                        alt={`Upload ${index + 1}`}
                        className={`rounded-2xl w-full ${
                          files.length === 1 ? 'h-auto max-h-[510px] object-contain bg-black' :
                          files.length === 2 ? 'h-[250px] object-cover' :
                          files.length === 3 ? (index === 0 ? 'h-[250px]' : 'h-[124px]') : 'h-[124px]'
                        } object-cover`}
                      />
                    )}
                    <button
                      onClick={() => {
                        const newFiles = [...files];
                        newFiles.splice(index, 1);
                        setFiles(newFiles);
                      }}
                      className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    {index === 3 && files.length > 4 && (
                      <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center">
                        <span className="text-white text-xl font-bold">+{files.length - 4}</span>
                      </div>
                    )}
                  </div>
                )).slice(0, 4)}
              </div>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex justify-between items-start px-4">
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="file"
              className="hidden"
              accept=".png,.jpg,.gif,.mp4,.jpeg"
              id="file"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const file = e?.target?.files?.[0];
                if (file) {
                  handleFileUpload(file);
                }
              }}
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                if (!isPollActive) {
                  const input = document.getElementById("file");
                  input?.click();
                }
              }}
              disabled={isPollActive}
            >
              <FiImage className="h-5 w-5" />
            </Button>
            <Button
              variant={isPollActive ? "default" : "ghost"}
              size="icon"
              onClick={() => setIsPollActive(!isPollActive)}
              disabled={files.length > 0}
            >
              <TbChecklist className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsScheduleModalOpen(true)}
              disabled={isPollActive || files.length > 0}
            >
              <Calendar className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsLocationModalOpen(true)}
              disabled={isPollActive || files.length > 0}
            >
              <MapPin className="h-5 w-4" />
            </Button>
            <Button
              variant={isBold ? "default" : "ghost"}
              size="icon"
              onClick={() => setIsBold(!isBold)}
              disabled={isPollActive}
            >
              <Bold className="h-5 w-5" />
            </Button>
            <Button
              variant={isItalic ? "default" : "ghost"}
              size="icon"
              onClick={() => setIsItalic(!isItalic)}
              disabled={isPollActive}
            >
              <Italic className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsChallengeModalOpen(true)}
              disabled={isPollActive || files.length > 0}
            >
              <Target className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsEventModalOpen(true)}
              disabled={isPollActive || files.length > 0}
            >
              <Ticket className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsSimplifiedMealModalOpen(true)}
              disabled={isPollActive || files.length > 0}
            >
              <Apple className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsExerciseFormAnalyzerModalOpen(true)}
              disabled={isPollActive || files.length > 0}
            >
              <Dumbbell className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              className="text-gray-500"
              onClick={() => setIsDraftsModalOpen(true)}
            >
              Drafts
            </Button>
            <Button onClick={handleSubmit}>
              {isPollActive ? "Create Poll" : scheduledTime ? "Schedule" : "Post"}
            </Button>
          </div>
        </CardFooter>
      </Card>
      <CreateChallengeModal 
        open={isChallengeModalOpen}
        onOpenChange={setIsChallengeModalOpen}
        onCreateChallenge={(challenge) => {
          if (!user) return;
          handlePost({
            userId: user.id,
            name: user.name || "",
            username: user.username || "",
            image: user.image || "",
            content: `Created a new challenge: ${challenge.title}\n\n${challenge.description}`,
            type: "challenge",
            likes: [],
            reposts: [],
            replies: [],
            files: [],
            views: [],
            viewCount: 0,
            originalPostId: null,
            communityId: null,
            challenge: challenge
          });
          setIsChallengeModalOpen(false);
          toast({
            title: "Challenge created successfully!",
          });
        }}
      />
      <CreateEventModal
        isOpen={isEventModalOpen}
        onClose={() => setIsEventModalOpen(false)}
      />
      <SchedulePostModal
        open={isScheduleModalOpen}
        onOpenChange={setIsScheduleModalOpen}
        onSchedule={(date) => {
          setScheduledTime(date);
          setScheduledTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
        }}
      />
      <DraftsModal
        open={isDraftsModalOpen}
        onOpenChange={setIsDraftsModalOpen}
        drafts={drafts}
        scheduledPosts={scheduledPosts}
        onEdit={handleEditPost}
        onDelete={handleDeletePost}
        onCancelScheduled={handleCancelScheduled}
      />
      <CreateLocationModal
        open={isLocationModalOpen}
        onOpenChange={setIsLocationModalOpen}
        onSelectLocation={(location) => setLocation(location)}
      />
      <CreateMealModal
        isOpen={isMealModalOpen}
        onClose={() => setIsMealModalOpen(false)}
        user={user}
      />
      <SimplifiedMealModal
        isOpen={isSimplifiedMealModalOpen}
        onClose={() => setIsSimplifiedMealModalOpen(false)}
        user={user}
      />
      <ExerciseFormAnalyzerModal
        isOpen={isExerciseFormAnalyzerModalOpen}
        onClose={() => setIsExerciseFormAnalyzerModalOpen(false)}
        user={user}
        onAnalysisComplete={(result) => {
          setExerciseAnalysisResult(result);
          
          // Create a post with the exercise analysis results
          if (user) {
            const exerciseType = result?.exercise_type?.charAt(0).toUpperCase() + result?.exercise_type?.slice(1);
            const score = result?.average_score?.toFixed(1);
            const repCount = result.rep_count;
            
            // Create content for the post
            const postContent = `I just analyzed my ${exerciseType} form!\n\nScore: ${score}%\nReps: ${repCount}\n\nCheck out my form analysis video below!`;
            
            // Set the content
            setContent(postContent);
            
            // Close the modal
            setIsExerciseFormAnalyzerModalOpen(false);
            
            // Show a toast notification
            toast({
              title: "Exercise form analysis complete!",
              description: `Your ${exerciseType} form scored ${score}%`,
            });
          }
        }}
      />
    </motion.div>
  );
};

export default CreatePost;
